import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import courthouseImage from "@/assets/practice-courthouse.jpg";

const testimonials = [
  { q: "We tripled our organic consultations in 9 months. The team understands personal injury economics, not just SEO.", role: "Managing Partner, PI Firm", loc: "Houston, TX" },
  { q: "Finally an SEO agency that doesn't pretend the algorithm changes every Tuesday. Honest, focused, effective.", role: "Founding Partner, Family Law Firm", loc: "Chicago, IL" },
  { q: "Our DUI practice went from invisible to #1 in the local pack. The reporting alone is worth what we pay.", role: "Partner, Criminal Defense Firm", loc: "Phoenix, AZ" },
];

export function TestimonialCarousel() {
  const [idx, setIdx] = useState(0);
  const touchX = useRef<number | null>(null);
  const n = testimonials.length;
  const go = (d: number) => setIdx((i) => (i + d + n) % n);

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Client testimonials"
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") go(-1);
        if (e.key === "ArrowRight") go(1);
      }}
    >
      <div
        className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_10px_30px_-18px_rgba(15,23,42,0.25)] md:grid md:grid-cols-[35%_65%]"
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}
      >
        <div className="relative h-56 md:h-auto md:min-h-[320px]">
          <img
            src={courthouseImage}
            alt="Classical courthouse facade with tall stone columns"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 hidden md:block bg-gradient-to-r from-transparent via-transparent to-card/70" />
        </div>
        <div className="flex flex-col p-6 sm:p-8 lg:p-12">
          <div aria-hidden className="font-display text-6xl leading-none text-[var(--gold-deep)] h-10">&ldquo;</div>
          <div className="relative grid" aria-live="polite">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                aria-hidden={i !== idx}
                className={`col-start-1 row-start-1 transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <blockquote className="font-display text-xl sm:text-2xl lg:text-[1.7rem] leading-snug text-foreground">{t.q}</blockquote>
                <figcaption className="mt-8 border-t border-border pt-5">
                  <div className="font-semibold text-foreground">{t.role}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{t.loc}</div>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous testimonial"
              className="grid h-11 w-11 place-items-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-foreground/40 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next testimonial"
              className="grid h-11 w-11 place-items-center rounded-full bg-[var(--gold-deep)] text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-2">
        {testimonials.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={`Show testimonial ${i + 1} of ${n}`}
            aria-current={i === idx}
            className={`h-2.5 w-2.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2 ${i === idx ? "bg-[var(--gold-deep)]" : "bg-border"}`}
          />
        ))}
      </div>
    </div>
  );
}
